<script setup>
import { computed, ref, watch, nextTick, onMounted, inject } from 'vue';
import { useI18n } from 'vue-i18n';
import { NODE_CONFIG, TRIGGER_EVENTS, ACTION_TYPES, DELAY_UNITS } from '../../constants/nodeTypes.js';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import { useFlowBuilderStore } from '../../store/index.js';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { ACTION_FORM_REGISTRY } from './action-forms/registry.js';
import ConditionNodeForm from './action-forms/ConditionNodeForm.vue';
import DelayNodeForm from './action-forms/DelayNodeForm.vue';
import QueryDatabaseForm from './action-forms/QueryDatabaseForm.vue';
import HttpRequestForm from './action-forms/HttpRequestForm.vue';
import SetContextForm from './action-forms/SetContextForm.vue';
import FilterArrayForm from './action-forms/FilterArrayForm.vue';
import SwitchForm from './action-forms/SwitchForm.vue';
import WaitExternalEventForm from './action-forms/WaitExternalEventForm.vue';
import CallFlowForm from './action-forms/CallFlowForm.vue';
import DatatableForm from './action-forms/DatatableForm.vue';
import CodeNodeForm from './action-forms/CodeNodeForm.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import TriggerPickerModal from '../TriggerPickerModal.vue';
import MultiselectDropdown from 'shared/components/ui/MultiselectDropdown.vue';
import CaptainAssistantAPI from 'dashboard/api/captain/assistant';
import segmentsAPI from 'escaleshop/segments/api/index.js';
import JsonEditor from '../JsonEditor.vue';
import { extractJsonPaths } from '../../constants/flowVariables.js';

const props = defineProps({
  node: { type: Object, default: null },
});

const emit = defineEmits(['update', 'close']);

const { t } = useI18n();

const config = computed(() => props.node ? NODE_CONFIG[props.node.type] : null);
const configLabel = computed(() => config.value?.label ? t(config.value.label) : '');
const configDescription = computed(() => config.value?.description ? t(config.value.description) : '');

const actionType = computed(() =>
  props.node?.type === 'action'
    ? ACTION_TYPES.find(a => a.key === props.node.data.action_name)
    : null
);
const actionTypeLabel = computed(() => actionType.value?.label ? t(actionType.value.label) : '');
const actionTypeDescription = computed(() => actionType.value?.description ? t(actionType.value.description) : '');

const actionFormComponent = computed(() =>
  actionType.value ? (ACTION_FORM_REGISTRY[actionType.value.key] ?? null) : null
);

const showTriggerPicker = ref(false);
const triggerTabIndex = ref(0);

function utcTimeToLocal(utcTime) {
  if (!utcTime) return '';
  const [h, m] = utcTime.split(':').map(Number);
  const d = new Date();
  d.setUTCHours(h, m, 0, 0);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

function localTimeToUtc(localTime) {
  if (!localTime) return '';
  const [h, m] = localTime.split(':').map(Number);
  const d = new Date();
  d.setHours(h, m, 0, 0);
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`;
}
const selectedTriggerEvent = computed(
  () => TRIGGER_EVENTS.find(e => e.key === props.node?.data?.event) ?? null
);

const SCHEDULE_TYPE_OPTIONS = computed(() => [
  { id: 'once',     name: t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE.ONCE') },
  { id: 'daily',    name: t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE.DAILY') },
  { id: 'weekly',   name: t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE.WEEKLY') },
  { id: 'monthly',  name: t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE.MONTHLY') },
  { id: 'interval', name: t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE.INTERVAL') },
]);
const INTERVAL_UNIT_OPTIONS = computed(() => [
  { id: 'minutes', name: t('FLOW_BUILDER.CONFIG_PANEL.INTERVAL_UNIT.MINUTES') },
  { id: 'hours',   name: t('FLOW_BUILDER.CONFIG_PANEL.INTERVAL_UNIT.HOURS') },
]);
const WEEKDAY_OPTIONS = computed(() => [
  { id: 1, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.MONDAY') },
  { id: 2, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.TUESDAY') },
  { id: 3, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.WEDNESDAY') },
  { id: 4, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.THURSDAY') },
  { id: 5, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.FRIDAY') },
  { id: 6, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.SATURDAY') },
  { id: 0, name: t('FLOW_BUILDER.CONFIG_PANEL.WEEKDAY.SUNDAY') },
]);
const MONTHDAY_OPTIONS = computed(() =>
  Array.from({ length: 31 }, (_, i) => ({ id: i + 1, name: t('FLOW_BUILDER.CONFIG_PANEL.MONTHDAY', { day: i + 1 }) }))
);

const selectedScheduleType = computed(() =>
  SCHEDULE_TYPE_OPTIONS.value.find(o => o.id === (props.node?.data?.schedule_type ?? 'once')) ?? SCHEDULE_TYPE_OPTIONS.value[0]
);
const selectedWeekday = computed(() =>
  WEEKDAY_OPTIONS.value.find(o => o.id === (props.node?.data?.schedule_day ?? 1)) ?? WEEKDAY_OPTIONS.value[0]
);
const selectedMonthday = computed(() =>
  MONTHDAY_OPTIONS.value.find(o => o.id === (props.node?.data?.schedule_day ?? 1)) ?? MONTHDAY_OPTIONS.value[0]
);

const NAMED_TYPES = ['action', 'condition', 'delay', 'wait_business_hours', 'smart_delay', 'ai_condition', 'generate_message', 'summarize', 'captain', 'classify', 'query_database', 'for_each', 'http_request', 'set_context', 'filter_array', 'switch', 'call_flow', 'wait_external_event', 'datatable_query', 'datatable_insert', 'datatable_update', 'datatable_delete', 'code', 'recommend_product', 'send_catalog_product'];

// Captain assistants
const captainAssistants = ref([]);
const captainAssistantOptions = computed(() =>
  captainAssistants.value.map(a => ({ id: a.id, name: a.name }))
);
const selectedCaptainAssistant = computed(() => {
  const id = props.node?.data?.assistant_id;
  return captainAssistantOptions.value.find(a => a.id === id) ?? null;
});

async function loadCaptainAssistants() {
  try {
    const { data } = await CaptainAssistantAPI.get();
    captainAssistants.value = data?.payload ?? data ?? [];
  } catch {
    captainAssistants.value = [];
  }
}

const flowBuilderStore = useFlowBuilderStore();
const flowDynamicVariables = inject('flowDynamicVariables', ref([]));
const flowSettings = inject('flowSettings', ref({}));
const updateFlowSettings = inject('updateFlowSettings', () => {});
const flowList = inject('flowList', ref([]));

const dynamicVarOptions = computed(() =>
  flowDynamicVariables.value.map(v => ({ id: v.key.replace(/^variables\./, ''), name: v.label }))
);

const webhookUrl = computed(() => {
  const token = flowBuilderStore.currentFlow?.webhook_token;
  if (!token) return '';
  return `${window.location.origin}/webhooks/flow_builder/${token}`;
});

const webhookSecret = computed(() => flowBuilderStore.currentFlow?.webhook_secret ?? '');

function copyWebhookUrl() {
  if (webhookUrl.value) navigator.clipboard.writeText(webhookUrl.value);
}

function wrapVar(path) {
  return '{{' + path + '}}';
}

const sampleVarNome = '{{variables.nome}}';
const sampleEventMessage = '{{event.data.message}}';
const sampleEventSourceFlowId = '{{event.data.source_flow_id}}';
const itemVarSample = computed(() => `{{variables.${props.node?.data?.item_variable || 'item'}}}`);
const recommendVarSample = computed(() => `{{variables.${props.node?.data?.variable_name || 'recommended_product'}.title}}`);

const isWebhookTrigger = computed(() => props.node?.type === 'trigger' && props.node?.data?.event === 'webhook_received');

watch(isWebhookTrigger, val => {
  if (!val && triggerTabIndex.value === 1) triggerTabIndex.value = 0;
});


const samplePayloadRaw = computed(() => props.node?.data?.sample_payload ?? '');

const samplePayloadPaths = computed(() => {
  try {
    const parsed = JSON.parse(samplePayloadRaw.value);
    return extractJsonPaths(parsed).map(p => `event.data.${p}`);
  } catch {
    return [];
  }
});

const samplePayloadError = computed(() => {
  if (!samplePayloadRaw.value.trim()) return '';
  try {
    JSON.parse(samplePayloadRaw.value);
    return '';
  } catch {
    return t('FLOW_BUILDER.CONFIG_PANEL.INVALID_JSON');
  }
});

// Teams (for classify)
const vueStore = useStore();
const storeTeams = useMapGetter('teams/getTeams');

const teamOptions = computed(() =>
  storeTeams.value.map(team => ({ value: team.id, label: team.name }))
);

const SEGMENT_TRIGGER_KEYS = ['contact_entered_segment', 'contact_left_segment'];

const segments = ref([]);
const segmentOptions = computed(() =>
  [{ id: null, name: t('FLOW_BUILDER.CONFIG_PANEL.ANY_SEGMENT') }, ...segments.value.map(s => ({ id: s.id, name: s.name }))]
);
const selectedSegment = computed(() => {
  const id = props.node?.data?.segment_id ?? null;
  return segmentOptions.value.find(s => s.id === id) ?? segmentOptions.value[0];
});

async function loadSegments() {
  try {
    const { data } = await segmentsAPI.getAll();
    segments.value = data.segments ?? [];
  } catch {
    segments.value = [];
  }
}

const errorFlowOptions = computed(() => [
  { id: null, name: t('FLOW_BUILDER.CONFIG_PANEL.NONE') },
  ...flowList.value.map(f => ({ id: f.id, name: f.name })),
]);
const selectedErrorFlow = computed(() =>
  errorFlowOptions.value.find(f => f.id === (flowSettings.value.error_flow_id ?? null)) ?? errorFlowOptions.value[0]
);

function addExtractField() {
  const fields = [...(props.node?.data?.fields ?? []), { key: '', description: '' }];
  emit('update', { ...props.node.data, fields });
}

function removeExtractField(index) {
  const fields = (props.node?.data?.fields ?? []).filter((_, i) => i !== index);
  emit('update', { ...props.node.data, fields });
}

function updateExtractField(index, prop, value) {
  const fields = (props.node?.data?.fields ?? []).map((f, i) => i === index ? { ...f, [prop]: value } : f);
  emit('update', { ...props.node.data, fields });
}

function addSetVariable() {
  const variables = [...(props.node?.data?.variables ?? []), { key: '', value: '' }];
  emit('update', { ...props.node.data, variables });
}

function removeSetVariable(index) {
  const variables = (props.node?.data?.variables ?? []).filter((_, i) => i !== index);
  emit('update', { ...props.node.data, variables });
}

function updateSetVariable(index, prop, value) {
  const variables = (props.node?.data?.variables ?? []).map((v, i) => i === index ? { ...v, [prop]: value } : v);
  emit('update', { ...props.node.data, variables });
}

watch(
  () => props.node?.type,
  type => {
    if (['captain', 'classify', 'ai_condition', 'generate_message', 'summarize', 'extract_data', 'recommend_product'].includes(type)) loadCaptainAssistants();
    if (type === 'classify') vueStore.dispatch('teams/get');
  },
  { immediate: true }
)

watch(
  () => props.node?.data?.event,
  event => {
    if (SEGMENT_TRIGGER_KEYS.includes(event)) loadSegments();
  },
  { immediate: true }
);
const isEditingName = ref(false);
const editingName = ref('');
const nameInputRef = ref(null);

watch(() => props.node?.id, () => { isEditingName.value = false; });

function startEdit() {
  editingName.value = props.node.data.name ?? configLabel.value;
  isEditingName.value = true;
  nextTick(() => nameInputRef.value?.focus());
}

function saveName() {
  const name = editingName.value.trim();
  if (name) updateData('name', name);
  isEditingName.value = false;
}

function cancelEdit() {
  isEditingName.value = false;
}

function updateData(key, value) {
  emit('update', { ...props.node.data, [key]: value });
}

function onTeamsChange(ids) {
  const selected = ids
    .map(id => storeTeams.value.find(team => team.id === id))
    .filter(Boolean)
    .map(team => ({ id: team.id, name: team.name }));
  emit('update', { ...props.node.data, teams: selected });
}

</script>

<template>
  <aside
    v-if="node"
    class="flex flex-col h-full w-[400px] min-w-[400px] shrink-0 border-l border-n-weak bg-n-surface-2 overflow-hidden"
  >
    <!-- Panel header -->
    <div class="flex items-center justify-between px-4 py-3 border-b border-n-weak shrink-0 gap-2">
      <div class="flex items-center gap-2 min-w-0 flex-1">
        <div v-if="node.type === '__flow_settings__'" class="flex items-center justify-center size-6 rounded-md shrink-0 bg-n-slate-6">
          <span class="i-lucide-settings-2 size-3.5 text-white" />
        </div>
        <div v-else class="flex items-center justify-center size-6 rounded-md shrink-0" :class="config?.color">
          <span :class="[config?.icon, 'size-3.5 text-white']" />
        </div>

        <!-- Editable name (action / condition / delay) -->
        <template v-if="NAMED_TYPES.includes(node.type)">
          <input
            v-if="isEditingName"
            ref="nameInputRef"
            v-model="editingName"
            class="flex-1 min-w-0 text-sm font-semibold text-n-slate-12 bg-transparent border-b border-n-brand outline-none"
            @keydown.enter="saveName"
            @keydown.esc="cancelEdit"
            @blur="saveName"
          />
          <template v-else>
            <span class="text-sm font-semibold text-n-slate-12 truncate">
              {{ node.data.name ?? configLabel }}
            </span>
            <button
              type="button"
              class="inline-flex items-center justify-center shrink-0 h-6 w-6 p-0 rounded-lg border-0 outline-1 outline outline-transparent text-n-slate-12 hover:bg-n-slate-9/10 active:scale-[0.97] transition-all duration-100 ease-out"
              @click="startEdit"
            >
              <span class="i-lucide-pencil size-3.5 flex-shrink-0" />
            </button>
          </template>
        </template>

        <!-- Non-editable (trigger, end, settings) -->
        <span v-else class="text-sm font-semibold text-n-slate-12 truncate">
          {{ node.type === '__flow_settings__' ? t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.TITLE') : configLabel }}
        </span>
      </div>

      <button
        type="button"
        class="inline-flex items-center justify-center size-8 rounded-lg text-n-slate-12 hover:bg-n-alpha-2 transition-all duration-100 ease-out active:scale-[0.97] shrink-0"
        @click="$emit('close')"
      >
        <span class="i-lucide-x size-4 flex-shrink-0" />
      </button>
    </div>

    <!-- Content -->
    <div class="flex-1 overflow-y-auto">
      <div class="flex flex-col p-4 gap-4">

        <!-- Trigger -->
        <template v-if="node.type === 'trigger'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-violet-500 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.EVENT') }}</span>
          <button
            type="button"
            class="flex items-center gap-2 w-full px-3 py-2 rounded-md border border-n-weak bg-n-slate-2 hover:bg-n-slate-3 transition-colors text-left"
            @click="showTriggerPicker = true"
          >
            <span class="flex items-center justify-center size-7 rounded-md shrink-0 bg-n-violet-3">
              <span :class="[selectedTriggerEvent?.icon ?? 'i-lucide-zap', 'size-3.5 text-n-violet-9']" />
            </span>
            <div class="flex-1 min-w-0">
              <span
                class="block text-sm truncate"
                :class="selectedTriggerEvent ? 'text-n-slate-12 font-medium' : 'text-n-slate-11'"
              >
                {{ selectedTriggerEvent?.label ? t(selectedTriggerEvent.label) : t('FLOW_BUILDER.CONFIG_PANEL.SELECT_TRIGGER') }}
              </span>
              <span v-if="selectedTriggerEvent" class="block text-xs text-n-slate-10 truncate">
                {{ t(selectedTriggerEvent.description) }}
              </span>
            </div>
            <span class="i-lucide-chevron-right size-3.5 text-n-slate-9 shrink-0" />
          </button>
          </div>

          <TriggerPickerModal
            :show="showTriggerPicker"
            @select="key => { updateData('event', key); showTriggerPicker = false; }"
            @close="showTriggerPicker = false"
          />

          <!-- Tabs -->
          <template v-if="selectedTriggerEvent">
            <woot-tabs class="-mx-4 [&_ul]:px-4" :index="triggerTabIndex" @change="i => triggerTabIndex = i">
              <woot-tabs-item :index="0" :name="t('FLOW_BUILDER.CONFIG_PANEL.TAB_CONFIG')" :show-badge="false" is-compact />
              <woot-tabs-item v-if="isWebhookTrigger" :index="1" name="JSON" :show-badge="false" is-compact />
            </woot-tabs>

            <!-- Tab JSON -->
            <div v-if="isWebhookTrigger && triggerTabIndex === 1" class="flex flex-col gap-3">
              <div class="flex flex-col gap-1.5">
                <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SAMPLE_PAYLOAD') }}</span>
                <JsonEditor
                  :model-value="samplePayloadRaw"
                  @update:model-value="val => emit('update', { ...node.data, sample_payload: val })"
                />
                <p v-if="samplePayloadError" class="text-xs text-n-ruby-11">{{ samplePayloadError }}</p>
              </div>

              <template v-if="samplePayloadPaths.length">
                <div class="h-px bg-n-weak" />
                <div class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FIELDS') }}</span>
                  <div class="flex flex-wrap gap-1.5 overflow-hidden">
                    <code
                      v-for="path in samplePayloadPaths"
                      :key="path"
                      class="text-xs bg-n-alpha-2 border border-n-weak rounded-md px-2 py-0.5 text-n-slate-11 font-mono cursor-pointer hover:bg-n-alpha-3 transition-colors select-all break-all"
                    >{{ wrapVar(path) }}</code>
                  </div>
                </div>
              </template>
            </div>

            <!-- Configuração -->
            <div v-if="triggerTabIndex === 0" class="flex flex-col gap-3">
              <template v-if="selectedTriggerEvent.key === 'scheduled'">
                <!-- Schedule type -->
                <div class="multiselect-wrap--small">
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SCHEDULE_TYPE_LABEL') }}</span>
                  </div>
                  <MultiselectDropdown
                    :options="SCHEDULE_TYPE_OPTIONS"
                    :selected-item="selectedScheduleType"
                    :has-thumbnail="false"
                    :hide-search="true"
                    @select="opt => updateData('schedule_type', opt?.id ?? 'once')"
                  />
                </div>

                <!-- Once: datetime -->
                <div v-if="(node.data.schedule_type ?? 'once') === 'once'" class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.DATE_TIME') }}</span>
                  <input
                    type="datetime-local"
                    class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    :value="node.data.scheduled_at ?? ''"
                    @input="updateData('scheduled_at', $event.target.value)"
                  />
                </div>

                <!-- Weekly: day of week -->
                <div v-if="node.data.schedule_type === 'weekly'" class="multiselect-wrap--small">
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.DAY_OF_WEEK') }}</span>
                  </div>
                  <MultiselectDropdown
                    :options="WEEKDAY_OPTIONS"
                    :selected-item="selectedWeekday"
                    :has-thumbnail="false"
                    :hide-search="true"
                    @select="opt => updateData('schedule_day', opt?.id ?? 1)"
                  />
                </div>

                <!-- Monthly: day of month -->
                <div v-if="node.data.schedule_type === 'monthly'" class="multiselect-wrap--small">
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.DAY_OF_MONTH') }}</span>
                  </div>
                  <MultiselectDropdown
                    :options="MONTHDAY_OPTIONS"
                    :selected-item="selectedMonthday"
                    :has-thumbnail="false"
                    :hide-search="true"
                    @select="opt => updateData('schedule_day', opt?.id ?? 1)"
                  />
                </div>

                <!-- Interval: value + unit -->
                <template v-if="node.data.schedule_type === 'interval'">
                  <div class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.REPEAT_EVERY') }}</span>
                    <div class="flex gap-2">
                      <input
                        type="number"
                        min="1"
                        class="w-20 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                        :value="node.data.interval_value ?? 30"
                        @input="updateData('interval_value', parseInt($event.target.value) || 30)"
                      />
                      <div class="multiselect-wrap--small flex-1">
                        <MultiselectDropdown
                          :options="INTERVAL_UNIT_OPTIONS"
                          :selected-item="INTERVAL_UNIT_OPTIONS.find(o => o.id === (node.data.interval_unit ?? 'minutes')) ?? INTERVAL_UNIT_OPTIONS[0]"
                          :has-thumbnail="false"
                          :hide-search="true"
                          @select="opt => updateData('interval_unit', opt?.id ?? 'minutes')"
                        />
                      </div>
                    </div>
                  </div>
                </template>

                <!-- Daily/Weekly/Monthly: time -->
                <div v-if="node.data.schedule_type && !['once', 'interval'].includes(node.data.schedule_type)" class="flex flex-col gap-1.5">
                  <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.TIME') }}</span>
                  <input
                    type="time"
                    class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                    :value="utcTimeToLocal(node.data.schedule_time)"
                    @input="updateData('schedule_time', localTimeToUtc($event.target.value))"
                  />
                </div>
              </template>

              <!-- Webhook trigger URL -->
              <template v-if="selectedTriggerEvent.key === 'webhook_received'">
                <div class="flex flex-col gap-3">
                  <!-- URL -->
                  <div class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.WEBHOOK_URL') }}</span>
                    <div class="flex items-center gap-2">
                      <code class="flex-1 text-xs bg-n-alpha-1 border border-n-weak rounded-lg px-2.5 py-2 break-all text-n-slate-11 select-all">
                        {{ webhookUrl || t('FLOW_BUILDER.CONFIG_PANEL.SAVE_FLOW_FOR_URL') }}
                      </code>
                      <button
                        v-if="webhookUrl"
                        type="button"
                        class="shrink-0 flex items-center justify-center size-8 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-2 transition-colors"
                        @click="copyWebhookUrl"
                      >
                        <span class="i-lucide-copy size-3.5 text-n-slate-11" />
                      </button>
                    </div>
                  </div>

                  <!-- HMAC secret -->
                  <div v-if="webhookSecret" class="flex flex-col gap-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SIGNATURE_REQUIRED') }}</span>
                    <p class="text-xs text-n-slate-10 leading-relaxed">
                      {{ t('FLOW_BUILDER.CONFIG_PANEL.SIGNATURE_HEADER_HINT') }}
                    </p>
                    <code class="text-[10px] font-mono bg-n-alpha-1 border border-n-weak rounded-lg px-2.5 py-2 text-n-slate-11 break-all">
                      {{ t('FLOW_BUILDER.CONFIG_PANEL.SIGNATURE_HEADER_SAMPLE') }}
                    </code>
                    <span class="text-xs font-medium text-n-slate-12 mt-1">{{ t('FLOW_BUILDER.CONFIG_PANEL.SECRET_LABEL') }}</span>
                    <div class="flex items-center gap-2">
                      <code class="flex-1 text-[10px] font-mono bg-n-alpha-1 border border-n-weak rounded-lg px-2.5 py-2 text-n-slate-11 break-all select-all">
                        {{ webhookSecret }}
                      </code>
                      <button
                        type="button"
                        class="shrink-0 flex items-center justify-center size-8 rounded-lg border border-n-weak bg-n-solid-1 hover:bg-n-alpha-2 transition-colors"
                        @click="() => navigator.clipboard.writeText(webhookSecret)"
                      >
                        <span class="i-lucide-copy size-3.5 text-n-slate-11" />
                      </button>
                    </div>
                  </div>
                </div>
              </template>

              <!-- order_updated: filtro de mudança de status -->
              <template v-if="selectedTriggerEvent.key === 'order_updated'">
                <div class="flex items-start justify-between gap-3">
                  <div class="flex flex-col gap-0.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ORDER_UPDATED_STATUS_FILTER') }}</span>
                    <p class="text-xs text-n-slate-9 leading-relaxed">{{ t('FLOW_BUILDER.CONFIG_PANEL.ORDER_UPDATED_STATUS_FILTER_HINT') }}</p>
                  </div>
                  <Switch
                    :model-value="node.data.only_on_status_change ?? false"
                    @change="updateData('only_on_status_change', !(node.data.only_on_status_change ?? false))"
                  />
                </div>
              </template>

              <!-- Segment trigger -->
              <template v-if="SEGMENT_TRIGGER_KEYS.includes(selectedTriggerEvent.key)">
                <div class="multiselect-wrap--small">
                  <div class="flex items-center justify-between mb-1.5">
                    <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SEGMENT') }}</span>
                  </div>
                  <MultiselectDropdown
                    :options="segmentOptions"
                    :selected-item="selectedSegment"
                    :has-thumbnail="false"
                    @select="opt => updateData('segment_id', opt?.id ?? null)"
                  />
                </div>
              </template>
            </div>

          </template>
        </template>

        <!-- Action: layout C -->
        <template v-if="node.type === 'action'">
          <!-- Title + description -->
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-blue-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ actionTypeLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ actionTypeDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />

          <!-- Dynamic form -->
          <component
            :is="actionFormComponent"
            v-if="actionFormComponent"
            :action-name="node.data.action_name"
            :model-value="node.data.action_params"
            @update:model-value="updateData('action_params', $event)"
          />
        </template>

        <!-- Delay -->
        <template v-if="node.type === 'delay'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-slate-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <DelayNodeForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Wait Business Hours -->
        <template v-if="node.type === 'wait_business_hours'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-slate-500 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
            <span class="i-lucide-info size-3.5 text-n-slate-9 shrink-0 mt-0.5" />
            <p class="text-xs text-n-slate-9 leading-relaxed">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.WAIT_BUSINESS_HOURS_INFO') }}
            </p>
          </div>
        </template>

        <!-- Smart Delay -->
        <template v-if="node.type === 'smart_delay'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-slate-500 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
            <span class="i-lucide-info size-3.5 text-n-slate-9 shrink-0 mt-0.5" />
            <p class="text-xs text-n-slate-9 leading-relaxed">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.SMART_DELAY_INFO') }}
            </p>
          </div>
        </template>

        <!-- Wait Reply -->
        <template v-if="node.type === 'wait_reply'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-slate-500 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.TIMEOUT') }}</span>
            <div class="flex gap-2">
              <input
                type="number"
                min="1"
                class="w-20 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
                :value="node.data.timeout_value ?? 1"
                @input="updateData('timeout_value', parseInt($event.target.value) || 1)"
              />
              <div class="multiselect-wrap--small flex-1">
                <MultiselectDropdown
                  :options="DELAY_UNITS.map(u => ({ id: u.key, name: t(u.label) }))"
                  :selected-item="{ id: node.data.timeout_unit ?? 'hours', name: DELAY_UNITS.find(u => u.key === (node.data.timeout_unit ?? 'hours'))?.label ? t(DELAY_UNITS.find(u => u.key === (node.data.timeout_unit ?? 'hours')).label) : '' }"
                  :has-thumbnail="false"
                  :hide-search="true"
                  @select="opt => updateData('timeout_unit', opt?.id ?? 'hours')"
                />
              </div>
            </div>
          </div>
          <div class="flex flex-col gap-1.5">
            <div class="flex items-center gap-2 text-xs text-n-slate-9">
              <span class="flex items-center gap-1"><span class="inline-block size-2 rounded-full bg-slate-400" /> {{ t('FLOW_BUILDER.CONFIG_PANEL.WAIT_REPLY_REPLIED') }}</span>
            </div>
            <div class="flex items-center gap-2 text-xs text-n-slate-9">
              <span class="flex items-center gap-1"><span class="inline-block size-2 rounded-full bg-n-ruby-9" /> {{ t('FLOW_BUILDER.CONFIG_PANEL.WAIT_REPLY_TIMEOUT') }}</span>
            </div>
          </div>
        </template>

        <!-- Condition -->
        <template v-if="node.type === 'condition'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-amber-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <ConditionNodeForm
            :model-value="node.data"
            :node-id="node.id"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- AI Condition -->
        <template v-if="node.type === 'ai_condition'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.QUESTION') }}</span>
            <textarea
              :value="node.data.question ?? ''"
              rows="3"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.QUESTION_PLACEHOLDER')"
              @input="updateData('question', $event.target.value)"
            />
            <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.CONFIG_PANEL.AI_CONDITION_HINT') }}</p>
          </div>
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.DEFAULT_INSTALLATION_MODEL')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null })"
            />
          </div>
        </template>

        <!-- Generate Message -->
        <template v-if="node.type === 'generate_message'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.AI_INSTRUCTION') }}</span>
            <textarea
              :value="node.data.prompt ?? ''"
              rows="4"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.GENERATE_MESSAGE_PLACEHOLDER')"
              @input="updateData('prompt', $event.target.value)"
            />
            <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.CONFIG_PANEL.GENERATE_MESSAGE_HINT') }}</p>
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SAVE_TO_VARIABLE') }}</span>
            <input
              type="text"
              :value="node.data.variable_name ?? ''"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.GENERATED_MESSAGE_PLACEHOLDER')"
              @input="updateData('variable_name', $event.target.value)"
            />
            <p class="text-xs text-n-slate-9">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.GENERATE_MESSAGE_VAR_HINT_PREFIX') }}
              <code class="text-n-slate-11">{{ sampleVarNome }}</code>
              {{ t('FLOW_BUILDER.CONFIG_PANEL.GENERATE_MESSAGE_VAR_HINT_SUFFIX') }}
            </p>
          </div>
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.DEFAULT_INSTALLATION_MODEL')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null })"
            />
          </div>
        </template>

        <!-- Summarize -->
        <template v-if="node.type === 'summarize'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.EXTRA_INSTRUCTION') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <textarea
              :value="node.data.context ?? ''"
              rows="2"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SUMMARIZE_PLACEHOLDER')"
              @input="updateData('context', $event.target.value)"
            />
          </div>
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.DEFAULT_INSTALLATION_MODEL')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null })"
            />
          </div>
          <div class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
            <span class="i-lucide-info size-3.5 text-n-slate-9 shrink-0 mt-0.5" />
            <p class="text-xs text-n-slate-9 leading-relaxed">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.SUMMARIZE_INFO') }}
            </p>
          </div>
        </template>

        <!-- Extract Data -->
        <template v-if="node.type === 'extract_data'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />

          <!-- Fields list -->
          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FIELDS_TO_EXTRACT') }}</span>
              <button
                type="button"
                class="inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-blue-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 px-2 text-xs active:enabled:scale-[0.97] justify-center"
                @click="addExtractField"
              >
                <span class="i-lucide-plus flex-shrink-0" />
                <span class="min-w-0 truncate">{{ t('FLOW_BUILDER.COMMON.ADD') }}</span>
              </button>
            </div>

            <div
              v-for="(field, i) in (node.data.fields ?? [])"
              :key="i"
              class="flex flex-col gap-1.5 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
            >
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  :value="field.key"
                  class="flex-1 h-7 rounded-md border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
                  :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.VARIABLE_NAME_PLACEHOLDER')"
                  @input="updateExtractField(i, 'key', $event.target.value)"
                />
                <button
                  type="button"
                  class="shrink-0 flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
                  @click="removeExtractField(i)"
                >
                  <span class="i-lucide-trash-2 size-3.5" />
                </button>
              </div>
              <input
                type="text"
                :value="field.description"
                class="w-full h-7 rounded-md border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9"
                :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.EXTRACT_FIELD_DESC_PLACEHOLDER')"
                @input="updateExtractField(i, 'description', $event.target.value)"
              />
            </div>

          </div>

          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.DEFAULT_INSTALLATION_MODEL')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null })"
            />
          </div>
        </template>

        <!-- Recommend Product -->
        <template v-if="node.type === 'recommend_product'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />

          <!-- Nome da variável -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.SAVE_TO_VARIABLE') }}</span>
            <input
              type="text"
              :value="node.data.variable_name ?? ''"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
              placeholder="recommended_product"
              @input="emit('update', { ...node.data, variable_name: $event.target.value })"
            />
            <p class="text-xs text-n-slate-9">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.RECOMMEND_PRODUCT_VAR_HINT') }}
              <code class="text-n-slate-11">{{ recommendVarSample }}</code>
            </p>
          </div>

          <!-- Instrução extra (opcional) -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.EXTRA_INSTRUCTION') }}
              <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span>
            </span>
            <textarea
              :value="node.data.context ?? ''"
              rows="2"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.RECOMMEND_PRODUCT_CONTEXT_PLACEHOLDER')"
              @input="emit('update', { ...node.data, context: $event.target.value })"
            />
          </div>

          <!-- Assistant (opcional) -->
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }}
              <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span>
            </span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.DEFAULT_INSTALLATION_MODEL')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null })"
            />
          </div>
        </template>

        <!-- Set Variables -->
        <template v-if="node.type === 'set_variables'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />

          <div class="flex flex-col gap-2">
            <div class="flex items-center justify-between">
              <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.VARIABLES') }}</span>
              <button
                type="button"
                class="inline-flex items-center min-w-0 gap-2 transition-all duration-100 ease-out border-0 rounded-lg outline-1 outline disabled:opacity-50 text-n-blue-11 hover:enabled:bg-n-alpha-2 focus-visible:bg-n-alpha-2 outline-transparent h-6 px-2 text-xs active:enabled:scale-[0.97] justify-center"
                @click="addSetVariable"
              >
                <span class="i-lucide-plus flex-shrink-0" />
                <span class="min-w-0 truncate">{{ t('FLOW_BUILDER.COMMON.ADD') }}</span>
              </button>
            </div>

            <div
              v-for="(variable, i) in (node.data.variables ?? [])"
              :key="i"
              class="flex flex-col gap-1.5 p-3 rounded-lg border border-n-weak bg-n-alpha-1"
            >
              <div class="flex items-center gap-2">
                <input
                  type="text"
                  :value="variable.key"
                  class="flex-1 h-7 rounded-md border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
                  :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.VARIABLE_KEY_PLACEHOLDER')"
                  @input="updateSetVariable(i, 'key', $event.target.value)"
                />
                <button
                  type="button"
                  class="shrink-0 flex items-center justify-center size-7 rounded-md text-n-slate-9 hover:text-n-ruby-11 hover:bg-n-alpha-2 transition-colors"
                  @click="removeSetVariable(i)"
                >
                  <span class="i-lucide-trash-2 size-3.5" />
                </button>
              </div>
              <input
                type="text"
                :value="variable.value"
                class="w-full h-7 rounded-md border border-n-weak bg-n-solid-1 px-2 text-xs text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
                :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.VARIABLE_VALUE_PLACEHOLDER')"
                @input="updateSetVariable(i, 'value', $event.target.value)"
              />
            </div>

          </div>
        </template>

        <!-- Captain -->
        <template v-if="node.type === 'captain'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }}</span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SELECT_ASSISTANT')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null, assistant_name: val?.name ?? null })"
            />
            <p v-if="!captainAssistantOptions.length" class="text-xs text-n-slate-9 italic">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.NO_ASSISTANT_FOUND') }}
            </p>
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.CONTEXT') }}</span>
            <textarea
              :value="node.data.context ?? ''"
              rows="3"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.CAPTAIN_CONTEXT_PLACEHOLDER')"
              @input="updateData('context', $event.target.value)"
            />
          </div>
        </template>

        <!-- Classify -->
        <template v-if="node.type === 'classify'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-teal-11 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ASSISTANT') }}</span>
            <MultiselectDropdown
              :options="captainAssistantOptions"
              :selected-item="selectedCaptainAssistant"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SELECT_ASSISTANT')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="val => emit('update', { ...node.data, assistant_id: val?.id ?? null, assistant_name: val?.name ?? null })"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.TEAMS') }}</span>
            <TagMultiSelectComboBox
              :model-value="(node.data.teams ?? []).map(t => t.id)"
              :options="teamOptions"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SELECT_TEAMS')"
              :search-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SEARCH_TEAM')"
              :empty-state="t('FLOW_BUILDER.CONFIG_PANEL.NO_TEAM_FOUND')"
              @update:model-value="onTeamsChange"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.CONTEXT') }}</span>
            <textarea
              :value="node.data.context ?? ''"
              rows="2"
              class="reset-base w-full px-3 py-2 rounded-lg bg-n-solid-1 outline outline-1 outline-n-weak focus:outline-n-brand text-sm text-n-slate-12 placeholder:text-n-slate-9 resize-none transition-all"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.CLASSIFY_CONTEXT_PLACEHOLDER')"
              @input="updateData('context', $event.target.value)"
            />
          </div>
        </template>

        <!-- For Each -->
        <template v-if="node.type === 'for_each'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />

          <!-- Lista -->
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.LIST_VARIABLE') }}</span>
            <MultiselectDropdown
              :options="dynamicVarOptions"
              :selected-item="dynamicVarOptions.find(o => o.id === node.data.variable_name) ?? null"
              :multiselector-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.SELECT_VARIABLE')"
              :input-placeholder="t('FLOW_BUILDER.COMMON.SEARCH')"
              :has-thumbnail="false"
              @select="v => updateData('variable_name', v?.id ?? '')"
            />
            <p v-if="!dynamicVarOptions.length" class="text-xs text-n-slate-9 italic">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.NO_VARIABLE_FOUND') }}
            </p>
          </div>

          <!-- Variável do item -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.ITEM_VARIABLE') }}</span>
            <input
              type="text"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.ITEM_PLACEHOLDER')"
              :value="node.data.item_variable ?? ''"
              @input="updateData('item_variable', $event.target.value)"
            />
            <p class="text-xs text-n-slate-9">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.ITEM_VARIABLE_HINT_PREFIX') }}
              <code class="text-n-slate-11">{{ itemVarSample }}</code>
              {{ t('FLOW_BUILDER.CONFIG_PANEL.ITEM_VARIABLE_HINT_SUFFIX') }}
            </p>
          </div>

          <!-- Variável do índice -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.INDEX_VARIABLE') }}</span>
            <input
              type="text"
              class="w-full h-9 rounded-lg border border-n-weak bg-n-solid-1 px-3 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand placeholder:text-n-slate-9 font-mono"
              :placeholder="t('FLOW_BUILDER.CONFIG_PANEL.ITEM_INDEX_PLACEHOLDER')"
              :value="node.data.item_index_variable ?? ''"
              @input="updateData('item_index_variable', $event.target.value)"
            />
          </div>

          <!-- Máx. iterações -->
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.MAX_ITERATIONS') }}</span>
            <input
              type="number"
              min="1"
              max="50"
              class="w-24 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :value="node.data.max_iterations ?? 10"
              @input="updateData('max_iterations', parseInt($event.target.value) || 10)"
            />
          </div>

          <!-- Info -->
          <div class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
            <span class="i-lucide-info size-3.5 text-n-slate-9 shrink-0 mt-0.5" />
            <p class="text-xs text-n-slate-9 leading-relaxed">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.FOR_EACH_INFO_PREFIX') }}
              <code class="text-n-slate-11">{{ itemVarSample }}</code>
              {{ t('FLOW_BUILDER.CONFIG_PANEL.FOR_EACH_INFO_SUFFIX') }}
            </p>
          </div>
        </template>

        <!-- HTTP Request -->
        <template v-if="node.type === 'http_request'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <HttpRequestForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Query Database -->
        <template v-if="node.type === 'query_database'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <QueryDatabaseForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Set Context -->
        <template v-if="node.type === 'set_context'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <SetContextForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Filter Array -->
        <template v-if="node.type === 'filter_array'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <FilterArrayForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Switch -->
        <template v-if="node.type === 'switch'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <SwitchForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Wait External Event -->
        <template v-if="node.type === 'wait_external_event'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-slate-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <WaitExternalEventForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Call Flow -->
        <template v-if="node.type === 'call_flow'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <CallFlowForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Datatable nodes -->
        <template v-if="['datatable_query', 'datatable_insert', 'datatable_update', 'datatable_delete'].includes(node.type)">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-green-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <DatatableForm
            :node-type="node.type"
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Code -->
        <template v-if="node.type === 'code'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-orange-600 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <CodeNodeForm
            :model-value="node.data"
            @update:model-value="emit('update', $event)"
          />
        </template>

        <!-- Flow Settings -->
        <template v-if="node.type === '__flow_settings__'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span class="i-lucide-settings-2 size-3.5 text-n-slate-9 shrink-0" />
              <span class="text-sm font-semibold text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.TITLE') }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.DESCRIPTION') }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
          <div class="multiselect-wrap--small flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ERROR_FLOW') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.COMMON.OPTIONAL') }}</span></span>
            <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ERROR_FLOW_HINT') }}</p>
            <MultiselectDropdown
              :options="errorFlowOptions"
              :selected-item="selectedErrorFlow"
              :has-thumbnail="false"
              :input-placeholder="t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.SEARCH_FLOW')"
              @select="opt => updateFlowSettings({ error_flow_id: opt?.id ?? null })"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ATTRIBUTION_WINDOW') }} <span class="text-n-slate-9 font-normal">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.DAYS') }}</span></span>
            <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ATTRIBUTION_WINDOW_HINT') }}</p>
            <input
              type="number"
              min="1"
              max="90"
              class="w-24 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :value="flowSettings.attribution_window_days ?? 7"
              @input="updateFlowSettings({ attribution_window_days: parseInt($event.target.value, 10) || 7 })"
            />
          </div>
          <div class="flex flex-col gap-1.5">
            <span class="text-sm font-medium text-n-slate-12">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.MAX_STEPS') }}</span>
            <p class="text-xs text-n-slate-9">{{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.MAX_STEPS_HINT') }}</p>
            <input
              type="number"
              min="10"
              max="5000"
              class="w-24 h-9 rounded-lg border border-n-weak bg-n-solid-1 px-2.5 text-sm text-n-slate-12 focus:outline-none focus:ring-1 focus:ring-n-brand"
              :value="flowSettings.max_steps ?? 200"
              @input="updateFlowSettings({ max_steps: parseInt($event.target.value, 10) || 200 })"
            />
          </div>
          <div class="flex items-start gap-2 p-3 rounded-lg bg-n-alpha-1 border border-n-weak">
            <span class="i-lucide-info size-3.5 text-n-slate-9 shrink-0 mt-0.5" />
            <p class="text-xs text-n-slate-9 leading-relaxed">
              {{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ERROR_FLOW_INFO_PREFIX') }}
              <code class="text-n-slate-11">{{ sampleEventMessage }}</code>
              {{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ERROR_FLOW_INFO_MID') }}
              <code class="text-n-slate-11">{{ sampleEventSourceFlowId }}</code>
              {{ t('FLOW_BUILDER.CONFIG_PANEL.FLOW_SETTINGS.ERROR_FLOW_INFO_SUFFIX') }}
            </p>
          </div>
        </template>

        <!-- End -->
        <template v-if="node.type === 'end'">
          <div class="flex flex-col gap-0.5">
            <div class="flex items-center gap-1.5">
              <span :class="[config?.icon, 'size-3.5 text-n-ruby-9 shrink-0']" />
              <span class="text-sm font-semibold text-n-slate-12">{{ configLabel }}</span>
            </div>
            <p class="text-xs text-n-slate-9 leading-relaxed pl-5">{{ configDescription }}</p>
          </div>
          <div class="h-px bg-n-weak -mx-4" />
        </template>

      </div>
    </div>
  </aside>
</template>
